import {type Client} from '@atproto/lex'
import {AtUri, type HandleString} from '@atproto/syntax'
import {type QueryClient, queryOptions, useQuery} from '@tanstack/react-query'

import {ERIS_IDENTITY, resolveErisProfileIdentifier} from '#/lib/eris-identity'
import {STALE} from '#/state/queries'
import {useAppviewClient} from '#/state/session'
import {com} from '#/lexicons'
import {useUnstableProfileViewCache} from './profile'

const RQKEY_ROOT = 'resolved-did'
export const RQKEY = (didOrHandle: string) => [RQKEY_ROOT, didOrHandle]

const resolvedDidQueryOptions = (
  client: Client,
  getUnstableProfile: (did: string) => {did: string} | undefined,
  didOrHandle: string | undefined,
) =>
  queryOptions({
    staleTime: STALE.HOURS.ONE,
    queryKey: RQKEY(didOrHandle ?? ''),
    queryFn: async () => {
      if (!didOrHandle) return ''
      const identifier = resolveErisProfileIdentifier(didOrHandle)
      // Just return the did if it's already one
      if (identifier.startsWith('did:')) return identifier

      /*
       * Resolution stays on the appview client: the old agent call was proxied
       * to the appview, and the PDS implementation is not equivalent for
       * handles hosted elsewhere.
       */
      const data = await client.call(com.atproto.identity.resolveHandle, {
        handle: identifier as HandleString,
      })
      return data.did
    },
    initialData: () => {
      // Return undefined if no did or handle
      if (!didOrHandle) return
      if (didOrHandle.toLocaleLowerCase() === ERIS_IDENTITY.handle) {
        return ERIS_IDENTITY.sourceDid
      }
      const profile = getUnstableProfile(didOrHandle)
      return profile?.did
    },
    enabled: !!didOrHandle,
  })

export function useResolveUriQuery(uri: string | undefined) {
  const urip = new AtUri(uri || '')
  const host = urip.host

  const client = useAppviewClient()
  const {getUnstableProfile} = useUnstableProfileViewCache()

  return useQuery({
    ...resolvedDidQueryOptions(client, getUnstableProfile, host),
    select: did => ({
      did,
      uri: AtUri.make(did, urip.collection, urip.rkey).toString(),
    }),
  })
}

export function useResolveDidQuery(didOrHandle: string | undefined) {
  const client = useAppviewClient()
  const {getUnstableProfile} = useUnstableProfileViewCache()

  return useQuery(
    resolvedDidQueryOptions(client, getUnstableProfile, didOrHandle),
  )
}

export function precacheResolvedUri(
  queryClient: QueryClient,
  handle: string,
  did: string,
) {
  queryClient.setQueryData<string>(
    RQKEY(handle),
    handle.toLocaleLowerCase() === ERIS_IDENTITY.handle
      ? ERIS_IDENTITY.sourceDid
      : did,
  )
}
